import { cloneElement, Children, ReactElement, forwardRef } from 'react'
import { useKeyboard } from '@react-aria/interactions'
import { useMenuTrigger } from '@react-aria/menu'
import Foco from 'react-foco'
import { useMenuTriggerState } from '@react-stately/menu'
import { MenuTriggerProps } from './Types'
import { Popover } from 'components/Popover/Popover'
import { filterDOMProps, mergeProps, useObjectRef } from '@react-aria/utils'
import { autoUpdate } from '@floating-ui/dom'
import {
  useFloating,
  flip,
  offset,
  inline,
  shift,
  useHover,
  FloatingPortal,
  safePolygon,
  useInteractions,
} from '@floating-ui/react-dom-interactions'
import Styles from './Submenu.module.scss'

export interface SubmenuTriggerHandler {}

const SubmenuTrigger = forwardRef<SubmenuTriggerHandler, MenuTriggerProps>(
  ({ children, showOverlay = true, placement = 'bottom-start', popoverClassName, popoverTargetRef, ...props }, _) => {
    const [trigger, menu] = Children.toArray(children)

    const ref = useObjectRef<HTMLElement>(null)
    const {
      x,
      y,
      reference: triggerRef,
      floating,
      context,
    } = useFloating({
      open: props.isOpen,
      placement: placement,
      strategy: 'fixed',
      onOpenChange: props.onOpenChange,
      middleware: [offset(4), inline(), flip(), shift({ padding: 4 })],
      whileElementsMounted: (reference, floating, update) => {
        return autoUpdate(reference, floating, update, {
          animationFrame: true,
        })
      },
    })

    const hover = useHover(context, {
      move: false,
      restMs: 150,
      handleClose: safePolygon(),
    })

    const { getReferenceProps, getFloatingProps } = useInteractions([hover])

    const state = useMenuTriggerState({
      ...props,
      onOpenChange: (isOpen) => {
        props.onOpenChange?.(isOpen)
        floating(null)
      },
    })
    const { menuTriggerProps, menuProps } = useMenuTrigger({ type: 'menu' }, state, ref)
    const { keyboardProps } = useKeyboard(props)

    const popover = (
      <Popover
        autoFocus={false}
        style={{
          position: 'fixed',
          top: y || 0,
          left: x || 0,
          pointerEvents: 'auto',
          visibility: x !== null && y !== null ? 'visible' : 'hidden',
        }}
        className={popoverClassName}
        onClose={() => props.onOpenChange?.(false)}
        isOpen={props.isOpen}
        ref={floating}
        isDismissable
        noOverlay
        {...getFloatingProps()}
        backdropClass={Styles.backdrop}
      >
        {cloneElement(
          menu as ReactElement,
          mergeProps(menuProps, {
            onClose: () => {
              props.onOpenChange?.(false)
            },
          }),
        )}
      </Popover>
    )

    return (
      <>
        {cloneElement(
          trigger as ReactElement,
          mergeProps(
            getReferenceProps({
              ...(trigger as ReactElement).props,
              ...keyboardProps,
              ...filterDOMProps(menuTriggerProps),
            }),
            {
              ref: (r: HTMLElement) => {
                ref.current = r
                triggerRef(r)
              },
            },
          ),
        )}
        {props.isOpen && (
          <>
            {showOverlay ? (
              <FloatingPortal id="floating-ui-root">{popover}</FloatingPortal>
            ) : (
              <Foco onClickOutside={() => props.onOpenChange?.(false)}>{popover}</Foco>
            )}
          </>
        )}
      </>
    )
  },
)

export { SubmenuTrigger }
